import { Alert, Button, Card, Col, Row, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import type { InspectionDeviceReadiness } from '../../modules/inspection-appointments/services/inspection-appointmentService';
import PlanDeviceReadinessDrawer from './PlanDeviceReadinessDrawer';
import { getMissingPlansInRange, loadDeviceReadiness } from './planDeviceReadiness';
import type { DeviceReadinessPeriod, DeviceReadinessScope } from './planDeviceReadiness';

const periods: Array<{ days: DeviceReadinessPeriod; label: string }> = [
  { days: 7, label: 'Haftalık' },
  { days: 30, label: 'Aylık' },
];

interface PlanDeviceReadinessCardProps {
  scope?: DeviceReadinessScope;
}

const PlanDeviceReadinessCard = ({ scope = 'all' }: PlanDeviceReadinessCardProps) => {
  const [today] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [plans, setPlans] = useState<InspectionDeviceReadiness[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState<DeviceReadinessPeriod | null>(null);
  const endDate = dayjs(today).add(29, 'day').format('YYYY-MM-DD');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    setPlans([]);
    loadDeviceReadiness(scope, today, endDate)
      .then((data) => { if (!cancelled) setPlans(data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scope, today, endDate, retry]);

  const counts = useMemo(() => ({
    7: getMissingPlansInRange(plans, today, 7).length,
    30: getMissingPlansInRange(plans, today, 30).length,
  }), [plans, today]);

  return (
    <>
      <Card bordered={false} style={{ borderRadius: 8, width: '100%', height: '100%', minHeight: 216 }} bodyStyle={{ padding: 20 }}>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 18, color: '#333', fontWeight: 700 }}>Planların Cihaz İhtiyaçları</div>
          <Typography.Text type="secondary">
            {scope === 'me' ? 'Eksik cihazı olan planlarınız' : 'Eksik cihazı olan planlar'}
          </Typography.Text>
        </div>
        {error ? (
          <Alert
            type="error"
            showIcon
            message="Planların cihaz ihtiyaçları yüklenemedi."
            action={<Button size="small" onClick={() => setRetry((value) => value + 1)}>Tekrar Dene</Button>}
          />
        ) : (
          <Row gutter={[16, 16]}>
            {periods.map((period) => {
              const color = counts[period.days] === 0 ? '#52c41a' : '#f5222d';
              return (
                <Col xs={24} md={12} key={period.days}>
                  <button
                    type="button"
                    className="dashboard-card-clickable"
                    aria-label={`${period.label} eksik cihazı olan planlar`}
                    aria-haspopup="dialog"
                    disabled={loading}
                    onClick={() => setSelectedPeriod(period.days)}
                    style={{
                      width: '100%', background: '#f7f8fb', border: 0, borderRadius: 8,
                      minHeight: 92, padding: 16, textAlign: 'left', fontFamily: 'inherit',
                      cursor: loading ? 'default' : 'pointer',
                    }}
                  >
                    <div style={{ borderLeft: `3px solid ${color}`, paddingLeft: 12 }}>
                      <div style={{ color: '#8c8c8c', fontSize: 13, minHeight: 22 }}>{period.label}</div>
                      <div style={{ color, fontSize: 24, fontWeight: 700 }}>
                        {loading ? <Spin size="small" /> : counts[period.days]}
                      </div>
                      <div style={{ color: '#8c8c8c', fontSize: 12 }}>Önümüzdeki {period.days} gün</div>
                    </div>
                  </button>
                </Col>
              );
            })}
          </Row>
        )}
      </Card>
      {selectedPeriod != null && (
        <PlanDeviceReadinessDrawer
          key={scope}
          scope={scope}
          periodDays={selectedPeriod}
          today={today}
          initialPlans={plans}
          onClose={() => setSelectedPeriod(null)}
        />
      )}
    </>
  );
};

export default PlanDeviceReadinessCard;
